import { savePermissions } from "@/helper/permissionHelper";
import useBranchStore from "@/stores/branch.store";
import {
  CreateRoleProfile,
  DeleteRoleProfile,
  GetCurrentAccess,
  GetRoleProfiles as GetNativeRoleProfiles,
  GetRoles as GetNativeRoles,
  UpdateRoleProfile,
  UpdateUserRoleProfiles as UpdateNativeUserRoleProfiles,
} from "@/api/permissionsApi";
import {
  buildAccessSnapshotFromRoles,
  buildDeniedAccessSnapshotFromRoles,
  isPermissionRoleFallbackEnabled,
} from "@/config/permissionManifest";
import { router } from "@/plugins/1.router";
import { axiosInstance } from "@/plugins/axios";
import { HttpClient } from "@/services/httpClient";
import { useAuthStore } from "@/stores/auth.store";
import { FilterParam } from "@/type";
import { Controllers, FrappeMethodType } from "@/type/api";
import { AddEditPermission, Permission, Role } from "@/type/Auth";
import { User } from "@/type/user";
import { passwordLogin } from "@/services/authService";

import type { Ref } from "vue";

const roleService = new HttpClient(Controllers.role);
const userResourceService = new HttpClient(Controllers.user);

// Standard response envelope (#16): the axios interceptor collapses a success
// envelope `{ ok, data }` to `{ message: <data> }`, and the backend wraps raw
// lists as `data.items` / `data.result`. This pulls the array out of any of the
// shapes a method endpoint may now return (legacy top-level `data` included).
function extractMethodList<T = any>(response: any): T[] {
  const candidates = [
    response?.message,
    response?.data,
    response,
  ];

  for (const value of candidates) {
    if (Array.isArray(value))
      return value as T[];
    if (Array.isArray(value?.items))
      return value.items as T[];
    if (Array.isArray(value?.result))
      return value.result as T[];
    if (Array.isArray(value?.data))
      return value.data as T[];
  }

  return [];
}

export async function GetLoggedUser(): Promise<string | null> {
  try {
    const response = await axiosInstance.get(
      "/api/method/frappe.auth.get_logged_user",
    );
    const user = response.data?.message;

    return typeof user === "string" && user ? user : null;
  } catch (error) {
    console.warn("Failed to fetch logged user", error);
    return null;
  }
}

export async function Login(
  loading: Ref<boolean>,
  loginBody: { usr: string; pwd: string },
  options: { remember?: boolean } = {},
): Promise<number> {
  loading.value = true;

  try {
    const loginResult = await passwordLogin({
      username: loginBody.usr,
      password: loginBody.pwd,
    }, {
      remember: options.remember,
    });

    const authStore = useAuthStore();
    let username = loginBody.usr;

    if (loginResult.strategy === "token") {
      authStore.setOAuthTokens(
        loginResult.data.access_token,
        loginResult.data.refresh_token ?? null,
        username,
      );

      const loggedUser = await GetLoggedUser();
      if (loggedUser && loggedUser !== "Guest")
        username = loggedUser;

      authStore.setOAuthTokens(
        loginResult.data.access_token,
        loginResult.data.refresh_token ?? null,
        username,
      );
    } else {
      const loggedUser = await GetLoggedUser();
      if (!loggedUser || loggedUser === "Guest")
        throw new Error("Session login did not persist");

      username = loggedUser;
      authStore.setSessionAuth(username);
    }

    try {
      const { GetUserProfile } = await import("@/api/userApi");
      const profileLoading = { value: false } as Ref<boolean>;
      const profile = await GetUserProfile(
        profileLoading,
        username,
      );

      if (profile)
        authStore.setUserProfile(profile);
    } catch (e) {
      console.warn("Failed to fetch user profile after login", e);
    }

    // fetch and persist permissions/roles right after setting auth so UI can react
    try {
      const { GetPermission } = await import("@/api/authApi");
      if (typeof GetPermission === "function") {
        await GetPermission();
      }
    } catch (e) {
      console.warn("Failed to fetch permissions after login", e);
    }

    return 200; // Success
  } catch (error: any) {
    useAuthStore().logout();

    if (error.response?.status) {
      return error.response.status;
    }
    console.error("Login request failed:", error);
    return 0; // fallback
  } finally {
    loading.value = false;
  }
}

export async function GetPermission() {
  const auth = useAuthStore();

  if (!auth.isAuthenticated) {
    auth.setPermissions([]);
    auth.setAccessSnapshot(null);
    return { message: [] as string[] };
  }

  // Refresh the branch (clinic) context alongside the access snapshot, on boot
  // and after login. Fire-and-forget: it must never block or fail permission
  // loading, and a missing backend method degrades to "no modal".
  void useBranchStore().load(true).catch(() => {});

  try {
    const backendSnapshot = await GetCurrentAccess();
    if (backendSnapshot) {
      const roles = savePermissions(backendSnapshot.roles);
      const snapshot = {
        ...backendSnapshot,
        roles,
      };

      auth.setAccessSnapshot(snapshot);

      // Permissions are what decide which offline packs this user earns, so this
      // is the first moment the device can know what to download. Fire-and-forget
      // and lazily imported: it must never block or fail the login, and the
      // offline modules must not join this file's startup import cycle.
      void import("@/offline/runtime")
        .then(({ prepareOfflineForUser }) => prepareOfflineForUser())
        .catch(error => console.warn("Offline preparation skipped:", error));

      return { message: roles, access: snapshot };
    }
  } catch (error) {
    console.warn("Failed to fetch dynamic access snapshot; using backend roles for admin detection", error);
  }

  try {
    const res = await axiosInstance.get(
      "/api/method/frappe.core.doctype.user.user.get_roles",
    );

    if (res.data) {
      const roles = savePermissions(res.data);
      const snapshot = isPermissionRoleFallbackEnabled()
        ? buildAccessSnapshotFromRoles(roles, auth.user)
        : buildDeniedAccessSnapshotFromRoles(roles, auth.user);

      auth.setAccessSnapshot(snapshot);
    }

    return res.data;
  } catch (error: any) {
    const status = error?.response?.status;

    if (status === 401) {
      auth.setPermissions([]);
      auth.setAccessSnapshot(null);
      router.push({ name: "login" });
    }
  }
}

export async function GetRoles(loading: Ref<boolean>) {
  loading.value = true;

  try {
    return (await GetNativeRoles()).map(role => ({ name: role.name })) as Role[];
  } catch (error: any) {
    console.error(error?.message || error);

    return null;
  } finally {
    loading.value = false;
  }
}

export async function GetProjectPermissions(
  loading: Ref<boolean>,
): Promise<Permission[] | null> {
  loading.value = true;

  try {
    return (await GetNativeRoleProfiles()) as unknown as Permission[];
  } catch (error) {
    console.error(error);

    return null;
  } finally {
    loading.value = false;
  }
}

export async function AddPermission(
  loading: Ref<boolean>,
  payload: AddEditPermission,
) {
  loading.value = true;

  try {
    return await CreateRoleProfile(payload);
  } catch (error) {
    console.error(error);

    return null;
  } finally {
    loading.value = false;
  }
}

export async function UpdatePermission(
  loading: Ref<boolean>,
  id: string,
  payload: AddEditPermission,
) {
  loading.value = true;

  try {
    return await UpdateRoleProfile(id, payload);
  } catch (error) {
    console.error(error);

    return null;
  } finally {
    loading.value = false;
  }
}

export async function DeletePermission(
  loading: Ref<boolean>,
  id: string,
): Promise<number> {
  loading.value = true;

  try {
    await DeleteRoleProfile(id);

    return 202;
  } catch (error) {
    console.error(error);

    return 0;
  } finally {
    loading.value = false;
  }
}

export async function GetUsers(
  loading: Ref<boolean>,
  filter: FilterParam,
  fields: string[] = [
    "name",
    "email",
    "full_name",
    "username",
    "enabled",
    "user_type",
    "user_image",
    "creation",
    "last_login",
  ],
): Promise<User[] | null> {
  loading.value = true;
  let res: User[] | null = null;
  const { search, ...rest } = filter;
  const limitStart = filter.limit_start ?? 0;
  const limitPageLength = filter.limit_page_length ?? 20;

  try {
    // `get_users_with_role_profile` takes AND-only `filters` and ignores `search`, so a typed
    // query never narrowed the list. Resolve the matching user names first (name/email, full
    // name or username — an OR the method cannot express), then fetch just those rows.
    const searchText = String(search ?? "").trim();
    let methodFilter: Record<string, any> = {
      ...rest,
      limit_start: limitStart,
      limit_page_length: limitPageLength,
    };

    if (searchText) {
      const names = await searchUserNames(searchText, limitStart, limitPageLength);

      if (!names.length)
        return [];

      methodFilter = {
        ...rest,
        filters: JSON.stringify([["name", "in", names]]),
        limit_start: 0,
        limit_page_length: names.length,
      };
    }

    const response = await roleService.get<any>(
      "pet_app.api.users.get_users_with_role_profile",
      methodFilter,
      undefined,
      JSON.stringify(fields),
      undefined,
      FrappeMethodType.method,
    );

    res = extractMethodList<User>(response);
  }
  catch (error: any) {
    console.error(error?.message ?? error);
  }
  finally {
    loading.value = false;
  }

  return res;
}

async function searchUserNames(
  search: string,
  limitStart: number,
  limitPageLength: number,
): Promise<string[]> {
  const like = `%${search}%`;
  const response = await userResourceService.get<{ data?: { name: string }[] }>(
    "",
    {
      filters: JSON.stringify([["name", "not like", "%@petapp.local"]]),
      or_filters: JSON.stringify([
        ["name", "like", like],
        ["full_name", "like", like],
        ["username", "like", like],
      ]),
      order_by: "name asc",
      limit_start: limitStart,
      limit_page_length: limitPageLength,
    },
    {},
    JSON.stringify(["name"]),
  );

  return (response?.data ?? []).map(row => row.name).filter(Boolean);
}
/**
 * Update a user's role_profiles by username (email)
 * @param username The username/email of the user
 * @param roleProfiles The array of role_profiles to set
 * @returns Promise<any>
 */
export async function updateUserRoleProfiles(
  username: string,
  roleProfiles: { role_profile: string }[],
): Promise<any> {
  return UpdateNativeUserRoleProfiles(username, roleProfiles);
}
